import React, { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Brand, BrandText } from "@/components/Brand";
import DemoTour from "@/components/DemoTour";
import ArabicKeyboard from "@/components/ArabicKeyboard";
import {
    House, IdentificationCard, Files, ShieldCheck, QrCode,
    Printer, ClipboardText, Warning, Gear, SignOut, Translate,
    ShieldStar, UserPlus, MagnifyingGlass, List, DotsThreeOutline,
} from "@phosphor-icons/react";

const roleMenu = {
    super_admin: ["dashboard", "applications", "approvals", "plates", "aluminum-plates", "scanner", "plate-lookup", "violations", "audit", "fraud", "admin", "demo-stickers", "demo-users", "security"],
    registrar: ["dashboard", "applications", "newApplication", "aluminum-plates", "security"],
    approver: ["dashboard", "approvals", "fraud", "security"],
    printer_operator: ["dashboard", "plates", "aluminum-plates", "security"],
    enforcement_officer: ["dashboard", "scanner", "plate-lookup", "security"],
    auditor: ["dashboard", "audit", "fraud", "plate-lookup", "violations", "security"],
    read_only: ["dashboard", "applications", "plates", "security"],
};

const bottomTabs = {
    super_admin: ["dashboard", "approvals", "scanner"],
    registrar: ["dashboard", "applications", "newApplication"],
    approver: ["dashboard", "approvals", "fraud"],
    printer_operator: ["dashboard", "plates", "aluminum-plates"],
    enforcement_officer: ["scanner", "plate-lookup", "dashboard"],
    auditor: ["dashboard", "violations", "audit"],
    read_only: ["dashboard", "applications", "plates"],
};

const iconMap = {
    dashboard: House,
    applications: Files,
    newApplication: ClipboardText,
    approvals: ShieldCheck,
    plates: IdentificationCard,
    scanner: QrCode,
    audit: ClipboardText,
    fraud: Warning,
    admin: Gear,
    "demo-stickers": QrCode,
    "demo-users": UserPlus,
    "plate-lookup": MagnifyingGlass,
    "aluminum-plates": IdentificationCard,
    violations: Warning,
    security: ShieldStar,
};

const linkMap = {
    dashboard: "/app",
    applications: "/app/applications",
    newApplication: "/app/applications/new",
    approvals: "/app/approvals",
    plates: "/app/plates",
    scanner: "/app/scanner",
    audit: "/app/audit",
    fraud: "/app/fraud",
    admin: "/app/admin",
    "demo-stickers": "/app/demo-stickers",
    "demo-users": "/app/demo-users",
    "plate-lookup": "/app/plate-lookup",
    "aluminum-plates": "/app/aluminum-plates",
    violations: "/app/violations",
    security: "/app/security",
};

function SidebarBody({ items, user, t, onNavigate, prefix }) {
    return (
        <>
                <div className="p-5 border-b border-[#2a2a2a]">
                    <Brand to="/app/dashboard" variant="dark" size="md" dataTestId={`${prefix}-brand`} />
                </div>
                <nav className="p-3 flex-1 space-y-1 overflow-y-auto">
                    {items.map((k) => {
                        const Icon = iconMap[k] || House;
                        return (
                            <NavLink
                                key={k}
                                to={linkMap[k]}
                                end={k === "dashboard"}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${
                                        isActive
                                            ? "bg-primary text-primary-foreground"
                                            : "text-white/75 hover:bg-white/5"
                                    }`
                                }
                                onClick={onNavigate}
                                data-testid={prefix === "sidebar" ? `nav-${k}` : `mobile-nav-${k}`}
                            >
                                <Icon size={18} weight="duotone" />
                                <span>{t(k)}</span>
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="p-3 border-t border-[#2a2a2a] text-xs text-white/60">
                    <div className="mb-1 font-semibold text-white">{user?.full_name}</div>
                    <div className="mono">{user?.email}</div>
                    <div className="mt-1 inline-block px-2 py-0.5 rounded bg-primary/20 text-primary-foreground text-[10px] tracking-widest uppercase">
                        {t(`role_${user?.role}`)}
                    </div>
                </div>
        </>
    );
}

function BottomTabBar({ tabs, t, onMore }) {
    const tabCls = "flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] leading-tight min-w-0 transition-colors";
    return (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#1a1a1a] border-t border-[#2a2a2a] flex pb-[env(safe-area-inset-bottom)]" data-testid="bottom-tab-bar">
            {tabs.map((k) => {
                const Icon = iconMap[k] || House;
                return (
                    <NavLink
                        key={k}
                        to={linkMap[k]}
                        end={k === "dashboard"}
                        className={({ isActive }) => `${tabCls} ${isActive ? "text-primary" : "text-white/70"}`}
                        data-testid={`bottom-tab-${k}`}
                    >
                        <Icon size={22} weight="duotone" />
                        <span className="truncate max-w-full px-1">{t(k)}</span>
                    </NavLink>
                );
            })}
            <button type="button" onClick={onMore} className={`${tabCls} text-white/70`} data-testid="bottom-tab-more">
                <DotsThreeOutline size={22} weight="duotone" />
                <span>{t("more")}</span>
            </button>
        </nav>
    );
}

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { t, lang, toggle, nextLangLabel } = useI18n();
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const items = roleMenu[user?.role] || ["dashboard"];
    const rtl = lang === "ar";

    return (
        <div className="min-h-screen bg-background text-foreground flex" data-testid="app-layout">
            <aside className="w-64 shrink-0 bg-[#1a1a1a] text-white border-e border-[#2a2a2a] hidden md:flex flex-col" data-testid="sidebar">
                <SidebarBody items={items} user={user} t={t} prefix="sidebar" />
            </aside>

            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetContent
                    side={rtl ? "right" : "left"}
                    className="w-72 max-w-[85vw] p-0 bg-[#1a1a1a] text-white border-[#2a2a2a] flex flex-col [&>button]:text-white"
                    data-testid="mobile-sidebar"
                >
                    <SheetTitle className="sr-only">Menu</SheetTitle>
                    <SidebarBody items={items} user={user} t={t} prefix="mobile-sidebar" onNavigate={() => setMenuOpen(false)} />
                </SheetContent>
            </Sheet>

            {/* Main */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="h-14 border-b border-border bg-card px-4 sm:px-6 flex items-center justify-between sticky top-0 z-10 backdrop-blur-xl" data-testid="topbar">
                    <div className="flex items-center gap-2 min-w-0">
                        <Button variant="ghost" size="icon" className="md:hidden shrink-0" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-toggle" aria-label="Open menu">
                            <List size={22} weight="bold" />
                        </Button>
                        <div className="text-xs tracking-[0.25em] uppercase text-muted-foreground truncate hidden sm:block">
                            {t("tagline")}
                        </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        <Button variant="ghost" size="sm" onClick={toggle} data-testid="lang-toggle" className="btn-press">
                            <Translate size={16} weight="bold" className="me-1" />
                            {nextLangLabel()}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn" className="btn-press">
                            <SignOut size={16} weight="bold" className="me-1" />
                            <span className="hidden sm:inline">{t("signOut")}</span>
                        </Button>
                    </div>
                </header>
                <main className="p-4 sm:p-6 lg:p-8 flex-1">{children}</main>
                <footer className="p-4 pb-20 md:pb-4 text-center text-xs text-muted-foreground border-t border-border">
                    © <BrandText className="text-xs" /> · {new Date().getFullYear()} · Government-grade platform
                </footer>
            </div>
            <BottomTabBar tabs={bottomTabs[user?.role] || ["dashboard"]} t={t} onMore={() => setMenuOpen(true)} />
            <DemoTour />
            <ArabicKeyboard />
        </div>
    );
}
